import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { ensureInteger } from "../common/utils/generalUtils";
import { complexTableQuery } from "../common/utils/tableUtilities";
import { markFetched } from "../common/hooks/initHook";

const key = client => ensureInteger(client.id);
const orientationKey = orientation => ensureInteger(orientation.id);

export const clientSlice = createSlice({
    name: 'clients',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, client) => { obj[key(client)] = client; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(c => state.data[key(c)] = c);
        },
        addOrUpdateOrientation: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byClient = groupBy(toUpdate, 'clientid');
            Object.entries(byClient).forEach(([ locationid, orientations ]) => {
                const client = state.data[locationid];
                if (client) {
                    const ids = new Set(orientations.map(o => orientationKey(o)));
                    client.orientations = (client.orientations?.filter(o => !ids.has(orientationKey(o))) ?? []).concat(orientations);
                }
            });
        },
        removeOrientation: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byClient = groupBy(toRemove, 'clientid');
            Object.entries(byClient).forEach(([ clientid, orientations ]) => {
                const client = state.data[clientid];
                if (client) {
                    const ids = new Set(orientations.map(o => orientationKey(o)));
                    client.orientations = (client.orientations?.filter(o => !ids.has(orientationKey(o))) ?? []);
                }
            });
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(client => delete state.data[key(client)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => clientSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'client_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'client_one');

const fetchAllClients = network => network.get('/api/v1/clients', clientSlice.actions.set, visualizeFailure('get'));

export const createClient = (network, client) => network.post('/api/v1/client', client, visualizeSuccess('create', clientSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateClient = (network, delta) => network.put('/api/v1/client', delta, clientSlice.actions.addOrUpdate, visualizeFailure('update'));
export const deleteClient = (network, id) => network.delete('/api/v1/client/' + window.encodeURIComponent(id), visualizeSuccess('remove', clientSlice.actions.remove), visualizeFailure('remove'));

export const addProhibitedMember = (network, memberid, clientid) => network.post('/api/v1/memberProhibitedClient', { memberid, clientid }, clientSlice.actions.addOrUpdate, DefaultFailureAction('create', 'prohibitedMembers'));
export const removeProhibitedMember = (network, memberid, clientid) => network.delete('/api/v1/memberProhibitedClient/' + encodeURIComponent(memberid) + '/' + encodeURIComponent(clientid), clientSlice.actions.addOrUpdate, DefaultFailureAction('remove', 'prohibitedMembers'));
// #endregion

// #region selectors
const selectClientData = state => state.clients.data;
const selectAllClients = createSelector([selectClientData], clients => Object.values(clients))
const selectClient = id => createSelector([selectClientData], clients => clients[id]);
const selectClients = ids => createSelector([selectClientData], clients => ids.reduce((res, id) => {
    if (clients[id]) {
        res.push(clients[id]);
    }
    return res;
}, []));
const selectMemberProhibitedClients = memberid => createSelector([selectClientData], clients => Object.values(clients).filter(c => c.prohibited_members?.includes(memberid)));
const selectSearchClients = search => createSelector([selectClientData], clients => {
    let ignoreFields = Object.keys(search).filter(f => search[f] === undefined || search[f] === null || (Array.isArray(search[f]) && search[f].length === 0));
    let results = Object.values(clients);
    return complexTableQuery(results, search, { ignoreFields });
});
// #endregion

const listen = {
    clientCreated: (client, { dispatch, websocket }) => {
        markFetched(websocket, 'client', key(client))
        dispatch(clientSlice.actions.addOrUpdate(client))
    },
    clientUpdated: (client, { dispatch }) => dispatch(clientSlice.actions.addOrUpdate(client)),
    clientRemoved: (client, { dispatch }) => dispatch(clientSlice.actions.remove(client)),
    clientOrientationCreated: (orientation, { dispatch }) => dispatch(clientSlice.actions.addOrUpdateOrientation(orientation)),
    clientOrientationUpdated: (orientation, { dispatch }) => dispatch(clientSlice.actions.addOrUpdateOrientation(orientation)),
    clientOrientationRemoved: (orientation, { dispatch }) => dispatch(clientSlice.actions.removeOrientation(orientation))
}
// #region listeners

const initClients = { run: fetchAllClients, listen, immortal: true, name: 'client' };
export const [useAllClients] = dataHookFactory(selectAllClients, initClients);
export const [useAllClientsMap] = dataHookFactory(selectClientData, initClients);
export const [useClients] = dataHookFactory(selectClients, ids => initClients);
export const [useClient, realizeClient] = dataHookFactory(selectClient, id => initClients);
export const [useMemberProhbitedClients] = dataHookFactory(selectMemberProhibitedClients, memberid => initClients);
export const [useClientSearch] = dataHookFactory(selectSearchClients, search => initClients);

const fullRoles = ['administrator', 'event manager', 'template manager'];
const readRoles = ['auditor', 'event auditor', 'template auditor', 'rostering manager', 'rostering auditor', 'recurring rostering manager', 'recurring rostering auditor', 'equipment manager', 'equipment auditor','timesheet manager','timesheet auditor', 'member manager', 'member auditor','accreditation auditor','training manager','training auditor'];
export const canUpdateClient = (credentials, offices) => checkPermissions(credentials, fullRoles, offices);
export const canCreateClient = (credentials) => checkPermissions(credentials, fullRoles);
export const canRemoveClient = canUpdateClient;
export const canViewClient = (credentials) => checkPermissions(credentials, fullRoles.concat(readRoles));
export const canRouteClients = ({credentials}) => canViewClient(credentials);

const checkPermissions = (credentials, requiredRoles, offices) => {
    return credentials.hasRole(requiredRoles, {office: offices});
}

const reducer = { name: clientSlice.name, reducer: clientSlice.reducer }
export default reducer;